import { readsApi } from './api';
import type {
  HeldSemantics,
  HoldBreakdown,
  TraceWalletMovement,
} from './ledger.service';

// ── Money composition dashboard ────────────────────────────────────────────
// "What is tax, what is driver money, what is held, and how much per merchant."
// Everything here lives in the FastAPI reads service (:8000) under /api/v1 and
// is gated on the chekit-level `wallets.read` permission.

/** The seven money buckets every wallet on the platform rolls up into. */
export type MoneyBucket =
  | 'tax'
  | 'driver'
  | 'merchant'
  | 'commission'
  | 'escrow'
  | 'customer'
  | 'platform';

export const MONEY_BUCKETS: MoneyBucket[] = [
  'escrow',
  'merchant',
  'commission',
  'driver',
  'tax',
  'customer',
  'platform',
];

/** Per-wallet-type detail inside a bucket. */
export interface MoneyCompositionWalletType extends HoldBreakdown {
  walletTypeCode: string;
  walletTypeName: string;
  walletCount: number;
  balance: number;
  held: number;
  available: number;
}

export interface MoneyCompositionRow extends HoldBreakdown {
  bucket: MoneyBucket;
  label: string;
  /** wallet_types.code values that feed this bucket — the audit trail for the number. */
  walletTypeCodes: string[];
  balance: number;
  held: number;
  /** Raw generated column (balance − held). Demoted to small print: it is
   *  negative for perfectly normal incoming escrow earmarks. */
  available: number;
  walletCount: number;
  byWalletType?: MoneyCompositionWalletType[];
}

export interface MoneyDashboardTotals extends HoldBreakdown {
  balance: number;
  held: number;
  available: number;
  walletCount?: number;
}

/** Emitted by the API ONLY when a bucket or wallet type is carrying STRANDED
 *  holds — earmarks against baskets that already finished. A negative raw
 *  `available` on its own is normal in-flight escrow and is never flagged. */
export interface MoneyFlag {
  level?: 'bucket' | 'walletType';
  bucket: MoneyBucket;
  walletTypeCode?: string;
  heldStranded: number;
  heldIncoming?: number;
  available?: number;
  severity?: 'danger';
  message: string;
  explanation?: string;
}

/** What was actually paid OUT of a bucket over the selected range, plus the
 *  wallet_transactions types that produced the figure so it can be audited. */
export interface SettledBucketRow {
  bucket: MoneyBucket;
  label: string;
  amount: number;
  transactionTypes: string[];
  transactionCount: number | null;
}

export interface MoneyDashboard {
  composition: MoneyCompositionRow[];
  totals: MoneyDashboardTotals;
  flags: MoneyFlag[];
  /** Plain-English copy for the hold breakdown, shipped by the API. */
  heldSemantics?: HeldSemantics;
  /** Per-bucket settled figures. The API may send this as an array or as an
   *  object keyed by bucket — use `normalizeSettled()` rather than reading it
   *  directly. */
  settled: unknown;
  from?: string | null;
  to?: string | null;
}

export interface MoneyDashboardParams {
  /** ISO date (YYYY-MM-DD) — bounds the `settled` figures only. */
  from?: string;
  to?: string;
}

/** Coerce the `settled` payload into rows.
 *
 *  The API returns a WRAPPER object — `{from, to, transactionTypes, rule,
 *  byBucket: [...], total}` — so the rows live under `byBucket`. Unwrap that
 *  first; iterating the wrapper's own keys would treat `from`/`rule`/`total`
 *  as buckets and render nothing. The array / bucket-keyed-map branches below
 *  are kept as tolerant fallbacks. */
export function normalizeSettled(settled: unknown): SettledBucketRow[] {
  if (!settled || typeof settled !== 'object') return [];

  const wrapper = settled as { byBucket?: unknown };
  if (wrapper.byBucket !== undefined && wrapper.byBucket !== null) {
    return normalizeSettled(wrapper.byBucket);
  }

  const entries: Array<[string, unknown]> = Array.isArray(settled)
    ? settled.map((row) => [
        (row as { bucket?: string })?.bucket ?? '',
        row,
      ])
    : Object.entries(settled as Record<string, unknown>);

  const rows: SettledBucketRow[] = [];

  for (const [key, raw] of entries) {
    if (typeof raw === 'number') {
      rows.push({
        bucket: key as MoneyBucket,
        label: key,
        amount: raw,
        transactionTypes: [],
        transactionCount: null,
      });
      continue;
    }
    if (!raw || typeof raw !== 'object') continue;

    const r = raw as Record<string, unknown>;
    const amount = [r.amount, r.settled, r.total, r.paidOut].find(
      (v) => typeof v === 'number',
    ) as number | undefined;
    // The API sends `sources: [{transactionType, walletTypeCode, amount, count}]`
    // rather than a flat string array. Derive the distinct transaction types from
    // it, otherwise the "what fed this figure" small print — the whole point of
    // the auditability requirement — renders blank.
    const fromSources = Array.isArray(r.sources)
      ? Array.from(
          new Set(
            (r.sources as Array<{ transactionType?: string }>)
              .map((s) => s?.transactionType)
              .filter((t): t is string => typeof t === 'string' && t.length > 0),
          ),
        )
      : undefined;
    const types =
      fromSources ??
      ([r.transactionTypes, r.types, r.transaction_types].find((v) =>
        Array.isArray(v),
      ) as string[] | undefined);
    const count = [r.transactionCount, r.count].find((v) => typeof v === 'number') as
      | number
      | undefined;

    rows.push({
      bucket: ((r.bucket as string) ?? key) as MoneyBucket,
      label: (r.label as string) ?? (r.bucket as string) ?? key,
      amount: amount ?? 0,
      transactionTypes: types ?? [],
      transactionCount: count ?? null,
    });
  }

  return rows;
}

// ── Per-merchant money ─────────────────────────────────────────────────────

export interface MerchantMoneyRow {
  businessId: string;
  businessName: string | null;
  walletBalance: number;
  walletHeld: number;
  /** Raw generated column (balance − held). Negative for normal in-flight escrow. */
  walletAvailable: number;
  /** Earmarked out of escrow against baskets still in flight. */
  walletHeldIncoming: number;
  /** Earmarked against baskets that already finished — the real anomaly. */
  walletHeldStranded: number;
  walletHeldOnBalance: number;
  /** balance − heldOnBalance, floored at 0. What the merchant can actually use. */
  walletSpendable: number;
  ordersConfirmed: number;
  grossSold: number;
  commissionCharged: number;
  netEarned: number;
}

export interface MerchantMoneyResponse {
  data: MerchantMoneyRow[];
  total: number;
  page: number;
  size: number;
  heldSemantics?: HeldSemantics;
}

export interface MerchantMoneyParams {
  page?: number;
  size?: number;
  q?: string;
}

// ── Split drilldown ────────────────────────────────────────────────────────
// The "boil it down to the simplest transaction" view: for one basket, how the
// goods money and the delivery money were each cut, and where each cut landed.

/** Which set of numbers is on screen.
 *  `released` — the immutable snapshot frozen onto the journal entry when escrow
 *  was released. Real money, already paid.
 *  `projected` — recomputed from the basket's own columns plus live config. What
 *  WOULD be paid. Present on every basket, released or not. */
export type SplitBasis = 'released' | 'projected';

/** Where a projected figure came from — or `unavailable`, which must never be
 *  rendered as 0. `released` is the synthetic source used when the figure came
 *  out of the release snapshot instead of a config lookup. */
export type SplitFigureSource = 'config' | 'unavailable' | 'released';

/** How `projected.delivery.riderPayout` was arrived at. `rider_delivery_payout`
 *  is written as 0.00 at checkout, so the API falls through a precedence chain. */
export type RiderPayoutSource =
  | 'stored'
  | 'derivedFromServiceFee'
  | 'config'
  | 'assumedFullFee';

export interface SplitCommissionParts {
  fromGoods: number;
  fromDelivery: number;
  total: number;
}

export interface SplitAllocations {
  merchant: number;
  commission: number;
  rider: number;
  tax: number;
  total: number;
  /** True when there was no rider wallet to pay, so the rider share was folded
   *  into commission rather than dropped. */
  riderFoldedIntoCommission?: boolean;
}

// ── The RELEASED snapshot (`computed`) ─────────────────────────────────────

export interface ReleasedGoods {
  /** Tax-inclusive goods total for this basket. */
  subtotal: number;
  /** subtotal × 100/116 — commission is always charged on this, never on VAT. */
  exVatBase: number;
  /** subtotal × 16/116 — the merchant keeps this and remits it themselves. */
  embeddedVat: number;
  commissionRate: number;
  commissionAmount: number;
  merchantNet: number;
  commissionMode?: string | null;
  vatToMerchant?: boolean;
}

export interface ReleasedDelivery {
  fee: number;
  driverPay: number;
  chekitCommission: number;
  commissionRate: number;
  vat: number;
  vatRate?: number | null;
  /** Who is on the hook for delivery VAT — `chekit`, `driver`, or none yet. */
  vatPayer: string | null;
  taxModel: string | null;
}

export interface SplitComputed {
  splitVersion?: unknown;
  goods: ReleasedGoods;
  delivery: ReleasedDelivery;
  commissionParts: SplitCommissionParts;
  allocations: SplitAllocations;
  /** Allocations sum to the basket total. False means the money does not add up. */
  reconciles: boolean;
  basis: 'released';
}

// ── The PROJECTED split (`projected`) — always present ─────────────────────

export interface ProjectedGoods {
  subtotal: number;
  exVatBase: number | null;
  embeddedVat: number | null;
  commissionRate: number | null;
  commissionAmount: number | null;
  merchantNet: number | null;
  commissionMode?: string | null;
  /** `unavailable` → rate/amount/merchantNet are null. Render "unknown", not 0. */
  commissionSource: 'config' | 'unavailable';
  commissionUnavailableReason?: string | null;
}

/** What the RELEASE path would actually pay from live config — not necessarily
 *  what checkout stored on the basket. */
export interface DeliveryConfigSplit {
  riderPayout: number;
  chekitCommission: number;
  vat: number;
}

export interface ProjectedDelivery {
  fee: number;
  chekitServiceFee: number;
  riderPayout: number;
  riderPayoutSource: RiderPayoutSource;
  chekitCommission: number;
  businessContribution: number;
  chekitContribution: number;
  providerType: string | null;
  /** Null when the delivery tax config could not be resolved. */
  vat: number | null;
  vatRate: number | null;
  taxModel: string | null;
  vatPayer: string | null;
  taxSource: 'config' | 'unavailable';
  configSplit: DeliveryConfigSplit | null;
  /** False → checkout stored a split the release path will not reproduce. */
  matchesReleasePath: boolean | null;
}

export interface ProjectedSplit {
  goods: ProjectedGoods;
  delivery: ProjectedDelivery;
  commissionParts: {
    fromGoods: number | null;
    fromDelivery: number;
    total: number | null;
  };
  allocations: {
    merchant: number | null;
    commission: number | null;
    rider: number;
    tax: number | null;
    total: number | null;
  };
  reconciles: boolean;
  basis: 'projected';
}

/** Who manually settled a stuck basket. Null unless ops completed it. */
export interface SplitCompletedBy {
  via: string | null;
  userId: string | null;
  name: string | null;
  reason: string | null;
  at: string | null;
}

export interface BasketSplit {
  basketId: string;
  businessId: string | null;
  businessName: string | null;
  status: string;
  subtotal?: number;
  discountAmount?: number;
  totalAmount: number;
  confirmedAt?: string | null;
  deliveredAt?: string | null;
  riderId?: string | null;
  riderUserId?: string | null;
  riderName?: string | null;
  deliveryProviderType?: string | null;
  /** What was frozen at release time. Null = never released. */
  snapshot: Record<string, unknown> | null;
  snapshotEntryId?: string | null;
  snapshotPostedAt?: string | null;
  /** Null until the basket is RELEASED. */
  computed: SplitComputed | null;
  /** Always sent — the figures that WOULD apply. Null only if the API could not
   *  read the config at all. */
  projected: ProjectedSplit | null;
  completedBy: SplitCompletedBy | null;
  walletMovements: TraceWalletMovement[];
}

// ── One shape for the renderer ─────────────────────────────────────────────
// The released snapshot and the projection carry the same figures under
// different names (`driverPay` vs `riderPayout`) and with different nullability.
// Normalise once so the waterfall is written once and the operator sees the same
// arithmetic either way — with `basis` saying which one they are looking at.

export interface SplitViewGoods {
  subtotal: number;
  exVatBase: number | null;
  embeddedVat: number | null;
  commissionRate: number | null;
  commissionAmount: number | null;
  merchantNet: number | null;
  commissionMode: string | null;
  commissionSource: SplitFigureSource;
  commissionUnavailableReason: string | null;
}

export interface SplitViewDelivery {
  fee: number;
  riderPayout: number | null;
  riderPayoutSource: RiderPayoutSource | null;
  chekitCommission: number | null;
  chekitServiceFee: number | null;
  businessContribution: number | null;
  chekitContribution: number | null;
  providerType: string | null;
  vat: number | null;
  vatRate: number | null;
  taxModel: string | null;
  vatPayer: string | null;
  taxSource: SplitFigureSource;
  configSplit: DeliveryConfigSplit | null;
  matchesReleasePath: boolean | null;
}

export interface SplitView {
  basis: SplitBasis;
  goods: SplitViewGoods;
  delivery: SplitViewDelivery;
  commissionParts: {
    fromGoods: number | null;
    fromDelivery: number | null;
    total: number | null;
  };
  allocations: {
    merchant: number | null;
    commission: number | null;
    rider: number | null;
    tax: number | null;
    total: number | null;
    riderFoldedIntoCommission?: boolean;
  };
  reconciles: boolean;
}

/** Pick the basis to render — the release snapshot when it exists, otherwise the
 *  projection — and flatten it into the single shape the waterfall consumes.
 *  Returns null only when a basket has neither, which means the API could not
 *  read the config AND the basket was never released. */
export function toSplitView(
  basket: Pick<BasketSplit, 'computed' | 'projected' | 'deliveryProviderType'>,
): SplitView | null {
  const released = basket.computed;

  if (released) {
    return {
      basis: 'released',
      goods: {
        subtotal: released.goods.subtotal,
        exVatBase: released.goods.exVatBase,
        embeddedVat: released.goods.embeddedVat,
        commissionRate: released.goods.commissionRate,
        commissionAmount: released.goods.commissionAmount,
        merchantNet: released.goods.merchantNet,
        commissionMode: released.goods.commissionMode ?? null,
        commissionSource: 'released',
        commissionUnavailableReason: null,
      },
      delivery: {
        fee: released.delivery.fee,
        riderPayout: released.delivery.driverPay,
        riderPayoutSource: null,
        chekitCommission: released.delivery.chekitCommission,
        // Not part of the release snapshot — fall back to the basket columns the
        // projection also reads, so the provider is still named.
        chekitServiceFee: basket.projected?.delivery.chekitServiceFee ?? null,
        businessContribution: basket.projected?.delivery.businessContribution ?? null,
        chekitContribution: basket.projected?.delivery.chekitContribution ?? null,
        providerType:
          basket.deliveryProviderType ?? basket.projected?.delivery.providerType ?? null,
        vat: released.delivery.vat,
        vatRate: released.delivery.vatRate ?? null,
        taxModel: released.delivery.taxModel,
        vatPayer: released.delivery.vatPayer,
        taxSource: 'released',
        configSplit: null,
        matchesReleasePath: null,
      },
      commissionParts: released.commissionParts,
      allocations: released.allocations,
      reconciles: released.reconciles,
    };
  }

  const p = basket.projected;
  if (!p) return null;

  return {
    basis: 'projected',
    goods: {
      subtotal: p.goods.subtotal,
      exVatBase: p.goods.exVatBase,
      embeddedVat: p.goods.embeddedVat,
      commissionRate: p.goods.commissionRate,
      commissionAmount: p.goods.commissionAmount,
      merchantNet: p.goods.merchantNet,
      commissionMode: p.goods.commissionMode ?? null,
      commissionSource: p.goods.commissionSource,
      commissionUnavailableReason: p.goods.commissionUnavailableReason ?? null,
    },
    delivery: {
      fee: p.delivery.fee,
      riderPayout: p.delivery.riderPayout,
      riderPayoutSource: p.delivery.riderPayoutSource,
      chekitCommission: p.delivery.chekitCommission,
      chekitServiceFee: p.delivery.chekitServiceFee,
      businessContribution: p.delivery.businessContribution,
      chekitContribution: p.delivery.chekitContribution,
      providerType: p.delivery.providerType ?? basket.deliveryProviderType ?? null,
      vat: p.delivery.vat,
      vatRate: p.delivery.vatRate,
      taxModel: p.delivery.taxModel,
      vatPayer: p.delivery.vatPayer,
      taxSource: p.delivery.taxSource,
      configSplit: p.delivery.configSplit,
      matchesReleasePath: p.delivery.matchesReleasePath,
    },
    commissionParts: p.commissionParts,
    allocations: p.allocations,
    reconciles: p.reconciles,
  };
}

/** Plain-English label for where a rider payout figure came from. */
export const RIDER_PAYOUT_SOURCE_LABEL: Record<RiderPayoutSource, string> = {
  stored: 'stored on the basket at checkout',
  derivedFromServiceFee: 'derived from the stored service fee (fee − Chekit service fee)',
  config: 'computed from the live delivery config',
  assumedFullFee: 'no split recorded — the whole fee is assumed to be the rider’s',
};

/** Order-level check across every basket. */
/** Exactly what the API returns — it does NOT send a single `reconciles` flag.
 *  Allocations can only be checked once EVERY basket has been released, so a
 *  partially-released order must not be reported as a failure. Use
 *  `summariseReconciliation()` to turn this into a verdict. */
export interface SplitReconciliation {
  orderTotal: number;
  sumBasketTotals: number;
  sumAllocations: number;
  basketCount: number;
  basketsWithSnapshot: number;
  basketsWithoutSnapshot: number;
  basketTotalsMatchOrderTotal: boolean;
  allocationsMatchOrderTotal: boolean;
}

export interface ReconciliationVerdict {
  /** Only false when something genuinely disagrees — never merely "not yet released". */
  reconciles: boolean;
  fullyReleased: boolean;
  message: string;
}

export function summariseReconciliation(r?: SplitReconciliation | null): ReconciliationVerdict {
  if (!r) return { reconciles: true, fullyReleased: false, message: 'No reconciliation data.' };

  const fullyReleased = r.basketCount > 0 && r.basketsWithoutSnapshot === 0;
  const totalsOk = r.basketTotalsMatchOrderTotal;
  // Unreleased baskets have no allocations yet — that is pending, not broken.
  const allocationsOk = fullyReleased ? r.allocationsMatchOrderTotal : true;

  let message: string;
  if (!totalsOk) {
    const diff = r.sumBasketTotals - r.orderTotal;
    message = `Basket totals are off by ${diff.toFixed(2)} against the order total.`;
  } else if (!fullyReleased) {
    message =
      `${r.basketsWithSnapshot} of ${r.basketCount} baskets released — allocations can only ` +
      `be checked once every basket has been released.`;
  } else if (!r.allocationsMatchOrderTotal) {
    const diff = r.sumAllocations - r.orderTotal;
    message = `Allocated total is off by ${diff.toFixed(2)} against the order total.`;
  } else {
    message = 'Baskets and allocations both reconcile to the order total.';
  }

  return { reconciles: totalsOk && allocationsOk, fullyReleased, message };
}

export interface SplitOrderHeader {
  id: string;
  status: string | null;
  paymentStatus: string | null;
  totalAmount: number;
  customerId: string | null;
  createdAt: string | null;
}

export interface OrderSplits {
  /** The API nests the order under `order` — `orderId` is only ever a legacy
   *  alias, so read `order.id` first. */
  order: SplitOrderHeader;
  orderId?: string;
  baskets: BasketSplit[];
  reconciliation: SplitReconciliation;
  /** Set when a basket id was supplied — it resolves up to its parent order. */
  resolvedFrom?: 'order' | 'basket';
  focusBasketId?: string | null;
}

// ── Service → FastAPI reads :8000 ──────────────────────────────────────────
export const moneyDashboardService = {
  /** Current composition of every shilling on the platform, plus what settled
   *  out of each bucket between `from` and `to`. */
  getDashboard: (params?: MoneyDashboardParams) =>
    readsApi.get<MoneyDashboard>(
      '/admin/money-dashboard',
      params as Record<string, unknown>,
    ),

  /** Per-merchant wallet position and trading figures, paginated. */
  listMerchants: (params?: MerchantMoneyParams) =>
    readsApi.get<MerchantMoneyResponse>(
      '/admin/money-dashboard/merchants',
      params as Record<string, unknown>,
    ),

  /** Full split arithmetic for one order. Accepts an order id OR a basket id. */
  getOrderSplits: (orderOrBasketId: string) =>
    readsApi.get<OrderSplits>(`/admin/splits/orders/${orderOrBasketId}`),
};
